import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { mediaUrl, uploadChatFiles, REACTION_EMOJIS } from '../api/chat';
import { onAttachmentClick } from '../download';
import { listCustMessages, sendCustReply, custMute, custMuteAll, custReact, custPinConversation, createCustConversation } from '../api/customerChat';
import { useAuth } from '../context/AuthContext';
import { searchCustomers } from '../api/customers';
import { fmtSize } from '../utils/chatFiles';
import ImageLightbox from './ImageLightbox';
import { linkifyText } from '../utils/linkify';
import { getSocket } from '../socket';
import { SVC_ORDER, SVC_SHORT, SVC_LABEL, groupByCustomer, channelsOfSameCustomer } from '../utils/custChannels';
import { useToast } from './Toast';
import EmojiPicker from './EmojiPicker';

// ===== Chat KHÁCH HÀNG (tab "Khách hàng") =====
// Giao diện + thao tác Y HỆT chat nội bộ (dùng chung class CSS chat*), chỉ khác nguồn dữ liệu:
// KH nhắn từ web/app bidnhanh đổ vào qua webhook; NV trả lời sẽ đẩy ngược sang bidnhanh.
// Các helper nhỏ dưới đây LẶP LẠI từ ChatWidget.jsx (không import chéo để tránh vòng import).

const EMOJI_ONLY = /^(?:[\p{Extended_Pictographic}‍️⃣\u{1F3FB}-\u{1F3FF}]|\s)+$/u;
function isBigEmoji(t) {
  if (!t) return false;
  const s = t.trim();
  if (!s || !EMOJI_ONLY.test(s)) return false;
  return [...s].filter((c) => !/\s/.test(c)).length <= 6;
}
const initials = (name) => (name || '?').trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (dt.toDateString() === new Date().toDateString()) return dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};
const fmtMsgTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const hm = dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  if (dt.toDateString() === now.toDateString()) return hm;
  const p = (n) => String(n).padStart(2, '0');
  const dm = `${p(dt.getDate())}/${p(dt.getMonth() + 1)}`;
  return dt.getFullYear() === now.getFullYear() ? `${hm} · ${dm}` : `${hm} · ${dm}/${dt.getFullYear()}`;
};
// Ảnh = type image/* HOẶC đuôi file là ảnh (nguồn bidnhanh hay thiếu type -> đừng coi ảnh là tệp).
const isImageAtt = (a) => (a.type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic|heif|svg|avif)(\?|#|$)/i.test(a.name || a.url || a.data || '');
function AttachmentView({ a }) {
  const src = mediaUrl(a.url || a.data);
  if (isImageAtt(a)) {
    return (
      <img className="chat-att-img" src={src} alt={a.name} title="Bấm để xem to" style={{ cursor: 'zoom-in' }} loading="lazy"
        onError={(e) => { e.currentTarget.classList.add('chat-att-img--broken'); e.currentTarget.removeAttribute('title'); e.currentTarget.alt = 'Ảnh không tải được'; }} />
    );
  }
  return (
    <a className="chat-att-file" href={src} download={a.name} target="_blank" rel="noreferrer" onClick={onAttachmentClick(src, a.name)}>
      📎 <span className="chat-att-name">{a.name}</span>
      <span className="chat-att-size">{fmtSize(a.size)}</span>
    </a>
  );
}

// Ai được thấy hội thoại KH "chưa gán" (đồng bộ backend accessScope): GĐ/CV + Phòng CSKH/DVKH.
export const canSeeAllKh = (me) =>
  me?.role === 'GD' || me?.role === 'CV' || (me?.departments || []).some((d) => ['Phòng CSKH', 'Phòng DVKH'].includes(d));

// Ai được TẮT ÂM thông báo KH (toàn bộ + từng khách): CHỈ Trưởng phòng & Giám đốc.
// Nhân viên KHÔNG có nút tắt thông báo (không cho tự tắt để khỏi bỏ lỡ tin khách).
export const canMuteKh = (me) => me?.role === 'GD' || me?.role === 'TP';

// Danh sách hội thoại KH — cùng kiểu dòng với danh sách nội bộ.
// Panel "Nhắn khách hàng mới" trong CỬA SỔ CHAT NỔI: tìm KH -> tạo/mở hội thoại -> vào chat luôn.
export function NewCustomerChat({ onDone }) {
  const { openConv, refreshCust } = useChat();
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    const t = q.trim();
    if (!t) { setOpts([]); return; }
    const id = setTimeout(() => searchCustomers(t, true).then(setOpts).catch(() => {}), 250);
    return () => clearTimeout(id);
  }, [q]);
  async function startChat(o) {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const conv = await createCustConversation(o.code);
      refreshCust();
      openConv({ ...conv, id: conv._id, kind: 'customer', name: conv.custName || conv.custCode || 'Khách hàng' });
      onDone && onDone();
    } catch (e) {
      setErr(e.response?.data?.message || 'Không tạo được hội thoại');
    } finally { setBusy(false); }
  }
  return (
    <div className="chatuserlist" style={{ padding: '8px 10px' }}>
      <input autoFocus style={{ width: '100%', boxSizing: 'border-box' }} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="🔍 Tìm mã / tên khách hàng…" />
      {err && <div className="small" style={{ color: 'var(--danger)', marginTop: 8 }}>{err}</div>}
      {!q.trim() && <div className="chatempty">Gõ mã hoặc tên KH để chủ động nhắn tin.</div>}
      {q.trim() && !opts.length && <div className="chatempty">Không tìm thấy khách hàng.</div>}
      {opts.slice(0, 30).map((o) => (
        <div key={o.id || o.code} className="chatuser-row" style={{ cursor: busy ? 'wait' : 'pointer' }} onClick={() => startChat(o)}>
          <span className="chat-avatar sm cust">{initials(o.name || o.code)}</span>
          <div className="chatuser-main">
            <div className="chatuser-name">{o.name || o.code}</div>
            <div className="chatuser-sub"><code>{o.code}</code>{o.sale ? ` · sale: ${o.sale}` : ''}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Chip các KÊNH của 1 khách trong dòng danh sách (KD/CSKH/Mua + số chưa đọc).
function ChannelChips({ group }) {
  return (
    <div className="cust-chips">
      {SVC_ORDER.filter((t) => group.chans[t]).map((t) => {
        const n = group.chans[t].unread || 0;
        return (
          <span key={t} className={'cust-chip' + (n ? ' has' : '')}>
            {SVC_SHORT[t]}{n > 0 && <b>{n > 9 ? '9+' : n}</b>}
          </span>
        );
      })}
    </div>
  );
}

export function CustomerConversationList({ q = '' }) {
  const { custConvs, openConv, windows, me, patchCustConv, drafts } = useChat();
  const { setMe } = useAuth();
  const admin = canSeeAllKh(me);
  const canMute = canMuteKh(me); // CHỈ TP/GĐ mới có nút tắt âm
  const [mutedAll, setMutedAll] = useState(!!me?.custPushMutedAll);
  useEffect(() => { setMutedAll(!!me?.custPushMutedAll); }, [me?.custPushMutedAll]);
  const toggleMuteAll = useCallback(async () => {
    const next = !mutedAll;
    setMutedAll(next); // cập nhật ngay cho mượt
    try {
      const r = await custMuteAll(next);
      setMutedAll(!!r.muted);
      setMe({ ...me, custPushMutedAll: !!r.muted }); // giữ trạng thái kể cả khi chuyển tab/remount
    } catch { setMutedAll(!next); } // lỗi -> hoàn lại
  }, [mutedAll, me, setMe]);
  const fold = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
  const kw = fold(q).trim();
  const listShown = kw ? custConvs.filter((c) => fold(c.name).includes(kw) || fold(c.custCode).includes(kw)) : custConvs;
  // GOM THEO KHÁCH: mỗi khách 1 dòng, các kênh thành chip. Mở dòng = mở kênh đại diện (có tin mới).
  const groups = groupByCustomer(listShown);
  const togglePinConv = async (e, c) => {
    e.stopPropagation();
    try {
      const r = await custPinConversation(c.id, !c.pinned);
      patchCustConv(c.id, { pinned: r.pinned });
    } catch (err) { alert(err?.response?.data?.message || 'Ghim thất bại'); }
  };
  if (!custConvs.length)
    return (
      <div className="chatempty">
        Chưa có khách hàng nào nhắn tin.
        <br />
        <span className="small muted">Tin nhắn từ web/app bidnhanh sẽ tự hiện ở đây.</span>
      </div>
    );
  if (kw && !groups.length) return <div className="chatempty">Không có khách hàng nào khớp “{q.trim()}”.</div>;
  return (
    <>
      {canMute && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
          <span className="small" style={{ fontWeight: 600, opacity: 0.85 }}>🔔 Thông báo tất cả khách</span>
          <button
            onClick={toggleMuteAll}
            title={mutedAll ? 'Đang TẮT thông báo mọi khách — bấm để bật lại' : 'Đang BẬT — bấm để tắt âm thông báo mọi khách'}
            style={{ fontSize: 12, padding: '4px 12px', borderRadius: 999, fontWeight: 700, border: 'none', cursor: 'pointer', color: '#fff', background: mutedAll ? 'var(--danger, #ef4444)' : 'var(--accent, #4f46e5)' }}
          >
            {mutedAll ? '🔕 Đang tắt' : '🔔 Đang bật'}
          </button>
        </div>
      )}
      <div className="chatlist">
      {groups.map((g) => {
        const rep = g.rep;
        const multi = g.list.length > 1;
        const draftText = g.list.map((c) => drafts?.[c.id]).find(Boolean); // nháp ở kênh bất kỳ của khách này
        const opened = g.list.some((c) => windows.some((w) => String(w.id) === String(c.id)));
        return (
          <div
            key={g.key}
            className={'chatlist-row' + (g.unread ? ' unread' : '') + (opened ? ' active' : '')}
            onClick={() => openConv(rep)}
          >
            <div className="chatlist-ava">
              <div className="chat-avatar cust">{initials(g.name)}</div>
              {g.unread > 0 && <span className="chatlist-dot" />}
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name">
                  {g.pinned ? '📌 ' : ''}
                  {g.name}
                  {admin && g.custSaleName ? <span className="small muted" style={{ fontWeight: 400 }}> · {g.custSaleName}</span> : null}
                  {admin && !multi && !rep.assignedTo && rep.svcType === 'kd' && <span className="chattag-warn">chưa gán</span>}
                </span>
                {draftText
                  ? <span className="chatlist-time" style={{ color: 'var(--danger)', fontWeight: 600 }}>Chưa gửi</span>
                  : <span className="chatlist-time">{fmtTime(g.lastMessage?.at)}</span>}
              </div>
              <div className="chatlist-preview">
                {multi && <span className="small muted">{SVC_SHORT[rep.svcType] || 'KD'}: </span>}
                {draftText || g.lastMessage?.text || 'Bắt đầu trò chuyện…'}
              </div>
              {multi && <ChannelChips group={g} />}
            </div>
            {g.unread > 0 && <span className="chatlist-badge">{g.unread}</span>}
            <button type="button" className={'chatlist-pinbtn' + (g.pinned ? ' on' : '')}
              title={g.pinned ? 'Bỏ ghim' : 'Ghim lên đầu (tối đa 3)'}
              onClick={(e) => togglePinConv(e, rep)}>📌</button>
          </div>
        );
      })}
      </div>
    </>
  );
}

// Dấu tích trạng thái tin NV gửi cho KH (kiểu Telegram), đè ở GÓC DƯỚI-PHẢI bong bóng:
//   đang gửi / chờ nối bidnhanh -> đồng hồ; 'sent' (đã tới bidnhanh) -> 1 tích;
//   'read' (KH đã nhận/đọc) -> 2 tích; lỗi thật -> dấu cảnh báo.
function tickState(m) {
  if (m.direction !== 'out') return null;
  if (m.pending || m.status === 'sending') return 'sending';
  if (m.status === 'read') return 'read';
  if (m.status === 'sent') return 'sent';
  if (m.status === 'failed') return m.error === 'not-configured' ? 'sending' : 'failed';
  return 'sending';
}
function DeliveryTick({ m }) {
  const st = tickState(m);
  if (!st) return null;
  const cfg = {
    sending: { c: '✓', cls: 'pending', title: 'Đang gửi…' },
    sent: { c: '✓', cls: 'sent', title: 'Đã gửi sang bidnhanh' },
    read: { c: '✓✓', cls: 'read', title: 'Khách đã nhận / đã đọc' },
    failed: { c: '!', cls: 'failed', title: 'Gửi lỗi — bấm để thử lại' },
  }[st];
  return <span className={'chatmsg-tick ' + cfg.cls} title={cfg.title}>{st === 'sending' ? '🕓' : cfg.c}</span>;
}

// Gộp danh sách tin từ server với state hiện tại — GIỮ tin đang gửi/gửi lỗi (clientId, pending/failed) và
// tin cũ đang đọc chưa có trong lô server. Dùng cho CẢ lần tải đầu lẫn poll dự phòng để đổi kênh/tải lại
// KHÔNG làm mất bong bóng "đang gửi"/"gửi lỗi" (không mất nút Gửi lại). Trả prev nếu không đổi (khỏi render lại).
function custMergeLatest(prev, msgs) {
  const serverIds = new Set(msgs.map((m) => String(m._id)));
  const pending = prev.filter((m) => m.clientId && (m.pending || m.failed) && !serverIds.has(String(m._id)));
  const oldestSrv = msgs.length ? new Date(msgs[0].createdAt).getTime() : 0;
  const olderKept = prev.filter((m) => !m.clientId && !serverIds.has(String(m._id)) && m.createdAt && new Date(m.createdAt).getTime() < oldestSrv);
  const next = [...olderKept, ...msgs, ...pending];
  if (next.length === prev.length && next.every((m, i) => String(m._id) === String(prev[i]?._id))) return prev;
  return next;
}

// Cửa sổ chat với 1 KH — cùng kiểu cửa sổ chat nội bộ.
// onTitleClick (chỉ ChatPage truyền): bấm TÊN KH trên đầu khung -> mở/đóng bảng thông tin bên phải.
export function CustomerChatWindow({ conv, front, onTitleClick, onBack, onSwitchChannel }) {
  const { me, custConvs, openConv, subscribeMessages, setConvMinimized, patchCustConv, bumpCustConv, closeWindow, setOpen, setDraft } = useChat();
  // Các KÊNH của cùng khách (để hiện tab chuyển kênh). switchChannel: ChatPage đổi hội thoại đang xem,
  // widget mở cửa sổ kênh khác.
  const chans = channelsOfSameCustomer(conv, custConvs);
  const switchChannel = onSwitchChannel || openConv;
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 980;
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  // Giữ NỘI DUNG ĐANG SOẠN DỞ theo từng hội thoại KH (đổi khách quay lại vẫn còn + hiện "Chưa gửi").
  const [text, setText] = useState(() => { try { return localStorage.getItem('ft_draft_' + conv.id) || ''; } catch { return ''; } });
  const [atts, setAtts] = useState([]);
  const [replyTo, setReplyTo] = useState(null); // { id, senderName, text }
  const [imgView, setImgView] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [minUnread, setMinUnread] = useState(0);
  const [reactPickerFor, setReactPickerFor] = useState(null);
  // Bảng cảm xúc: đóng khi bấm ra ngoài (không đóng khi di chuột) — bỏ onMouseLeave để bấm được emoji.
  useEffect(() => {
    if (!reactPickerFor) return;
    const close = (e) => { if (!e.target.closest('.chatmsg-react-picker')) setReactPickerFor(null); };
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', close); };
  }, [reactPickerFor]);
  // GIỮ (long-press) trên mobile = Trả lời (corner ẩn trên mobile); chạm nhanh = thả cảm xúc.
  const lpTimer = useRef(null);
  const lpFired = useRef(false);
  const [peek, setPeek] = useState(null);
  const [newBelow, setNewBelow] = useState(0);
  const [scrolledUp, setScrolledUp] = useState(false); // cuộn xa đáy -> luôn cho nút về tin mới nhất
  const [size, setSize] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('ft_chat_size') || 'null');
      if (s && s.w && s.h) return { w: s.w, h: s.h };
    } catch {}
    return null;
  });
  const emojiBtnRef = useRef(null);
  const minimizedRef = useRef(false);
  const endRef = useRef(null);
  const msgsRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const unreadRef = useRef(null);
  const initRef = useRef(false);
  const unreadSnapRef = useRef(conv.unread || 0);

  const myId = String(me.id || me._id);

  const toggleMinimize = useCallback((next) => {
    const on = typeof next === 'boolean' ? next : !minimizedRef.current;
    minimizedRef.current = on;
    setMinimized(on);
    setConvMinimized(conv.id, on);
    if (!on) { setMinUnread(0); import('../api/customerChat').then(({ custMarkRead }) => custMarkRead(conv.id).catch(() => {})); }
  }, [conv.id, setConvMinimized]);
  useEffect(() => () => setConvMinimized(conv.id, false), [conv.id, setConvMinimized]);

  // Tải tin khi mở cửa sổ.
  useEffect(() => {
    let alive = true;
    setLoadingMsgs(true);
    initRef.current = false;
    unreadSnapRef.current = conv.unread || 0;
    listCustMessages(conv.id)
      // GỘP thay vì thay sạch: đổi kênh/mở lại KHÔNG vứt bong bóng "đang gửi/gửi lỗi" (giữ nút Gửi lại).
      .then((msgs) => alive && setMessages((prev) => custMergeLatest(prev, msgs)))
      .finally(() => alive && setLoadingMsgs(false));
    return () => { alive = false; };
  }, [conv.id]);

  // Tin mới realtime (qua bus của ChatContext — sự kiện custchat:message).
  useEffect(() => {
    const unsub = subscribeMessages(conv.id, (payload) => {
      if (minimizedRef.current && payload.direction === 'in' && !payload.clientId) setMinUnread((n) => n + 1);
      setMessages((prev) => {
        if (payload.clientId && prev.some((m) => m.clientId && m.clientId === payload.clientId)) {
          return prev.map((m) => (m.clientId === payload.clientId ? { ...payload } : m));
        }
        return prev.some((m) => String(m._id) === String(payload._id)) ? prev : [...prev, payload];
      });
    });
    return unsub;
  }, [conv.id, subscribeMessages]);

  // DỰ PHÒNG socket chập chờn: ~5s tải lại + gộp (giữ tin đang gửi/lỗi) — giống chat nội bộ.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      listCustMessages(conv.id)
        .then((msgs) => setMessages((prev) => custMergeLatest(prev, msgs)))
        .catch(() => {});
    };
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [conv.id]);

  // Cảm xúc realtime.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onReaction = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((m) => (String(m._id) === String(p.messageId) ? { ...m, reactions: p.reactions } : m)));
    };
    // Trạng thái giao tin đổi (KH đã đọc -> 'read' = 2 tích).
    const onStatus = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      const ids = new Set((p.messageIds || []).map(String));
      setMessages((prev) => prev.map((m) => (ids.has(String(m._id)) ? { ...m, status: p.status } : m)));
    };
    socket.on('custchat:reaction', onReaction);
    socket.on('custchat:status', onStatus);
    return () => { socket.off('custchat:reaction', onReaction); socket.off('custchat:status', onStatus); };
  }, [conv.id]);

  const isNearBottom = () => {
    const el = msgsRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const jumpToLatest = () => {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setNewBelow(0);
  };

  // Lưu bản nháp theo hội thoại KH (chuyển khách không mất đoạn đang gõ + hiện "Chưa gửi" ở danh sách).
  useEffect(() => { setDraft(conv.id, text); }, [text, conv.id, setDraft]);

  // Cuộn: lần đầu tới tin chưa đọc; tin mới chỉ tự cuộn khi của mình hoặc đang ở gần đáy.
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const lastId = String(last.clientId || last._id);
    if (!initRef.current) {
      initRef.current = true;
      lastMsgIdRef.current = lastId;
      if (unreadRef.current) unreadRef.current.scrollIntoView({ block: 'start' });
      else endRef.current?.scrollIntoView();
      return;
    }
    if (lastMsgIdRef.current === lastId) return;
    lastMsgIdRef.current = lastId;
    if (String(last.sender?._id || '') === myId || isNearBottom()) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setNewBelow((n) => n + 1);
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (atts.length) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [atts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ô nhập tự cao theo nội dung (tối đa ~5 dòng) — giống chat nội bộ.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 112) + 'px';
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(fileList) {
    const items = [...fileList].map((f) => ({
      file: f, name: f.name, type: f.type, size: f.size,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setAtts((prev) => [...prev, ...items]);
  }
  function onPaste(e) {
    const imgs = [...(e.clipboardData?.items || [])].filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    if (imgs.length) { e.preventDefault(); addFiles(imgs); }
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  // Đóng bảng emoji khi click ra ngoài.
  useEffect(() => {
    if (!showEmoji) return;
    const onDown = (e) => {
      if (e.target.closest?.('.emoji-pop')) return;
      if (emojiBtnRef.current?.contains(e.target)) return;
      setShowEmoji(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showEmoji]);

  const insertEmoji = useCallback((emo) => {
    const el = inputRef.current;
    const start = el ? el.selectionStart : null;
    const end = el ? el.selectionEnd : null;
    setText((prev) => {
      const a = start ?? prev.length;
      const b = end ?? prev.length;
      return prev.slice(0, a) + emo + prev.slice(b);
    });
    setTimeout(() => {
      el?.focus();
      const p = (start ?? 0) + emo.length;
      el?.setSelectionRange(p, p);
    }, 0);
  }, []);

  const sendSticker = useCallback(async (emo) => {
    setShowEmoji(false);
    try {
      const msg = await sendCustReply(conv.id, emo, {});
      setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      bumpCustConv(conv.id, msg);
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || 'Gửi sticker thất bại');
    }
  }, [conv.id, bumpCustConv]);

  async function submit(e) {
    e?.preventDefault();
    const t = text.trim();
    if ((!t && !atts.length) || sending) return;
    const reply = replyTo;
    const replySnap = reply ? { message: reply.id, senderName: reply.senderName, text: reply.text } : null;
    const sentAtts = atts;
    setText('');
    setAtts([]);
    setReplyTo(null);

    // Đường nhanh: chỉ chữ -> hiện ngay (optimistic), thay bằng bản thật khi server trả về.
    if (!sentAtts.length) {
      const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const temp = { _id: clientId, clientId, direction: 'out', sender: { _id: myId, name: me.name }, senderName: me.name, text: t, attachments: [], replyTo: replySnap, createdAt: new Date().toISOString(), pending: true };
      setMessages((prev) => [...prev, temp]);
      try {
        const msg = await sendCustReply(conv.id, t, { clientId, replyTo: reply?.id });
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) {
            return prev.filter((m) => m.clientId !== clientId);
          }
          return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
        });
        bumpCustConv(conv.id, msg);
      } catch {
        setMessages((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true } : m)));
      }
      return;
    }

    // Có đính kèm: upload trước (dùng chung kho tệp chat nội bộ).
    setSending(true);
    try {
      const metas = await uploadChatFiles(sentAtts.map((a) => a.file));
      const msg = await sendCustReply(conv.id, t, { attachments: metas, replyTo: reply?.id });
      setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      bumpCustConv(conv.id, msg);
      sentAtts.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    } catch (err) {
      setText(t);
      setAtts(sentAtts);
      alert(err?.response?.data?.message || err?.message || 'Gửi tệp thất bại');
    } finally {
      setSending(false);
    }
  }

  const resend = useCallback(async (failed) => {
    setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: true, failed: false } : m)));
    try {
      const msg = await sendCustReply(conv.id, failed.text, { clientId: failed.clientId });
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...msg } : m)));
      bumpCustConv(conv.id, msg);
    } catch {
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: false, failed: true } : m)));
    }
  }, [conv.id, bumpCustConv]);

  // Tắt/bật âm hội thoại KH (riêng mình).
  const muted = !!conv.muted;
  const doMute = useCallback(async () => {
    try {
      const r = await custMute(conv.id, !muted);
      patchCustConv(conv.id, { muted: r.muted });
      toast(r.muted ? '🔕 Đã tắt âm hội thoại này' : '🔔 Đã bật lại thông báo');
    } catch (e) { toast(e?.response?.data?.message || 'Không đổi được', 'danger'); }
  }, [conv.id, muted, patchCustConv, toast]);

  const startReply = useCallback((m) => {
    const preview = m.text || ((m.attachments || []).length ? 'đã gửi tệp đính kèm' : '');
    setReplyTo({ id: m._id, senderName: m.direction === 'in' ? (m.senderName || conv.name) : (m.sender?.name || m.senderName || ''), text: String(preview).slice(0, 200) });
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); }
    }, 0);
  }, [conv.name]);

  // Thả/đổi/bỏ cảm xúc (chỉ tin không phải của mình) — cập nhật lạc quan.
  const doReact = useCallback((m, emoji) => {
    setReactPickerFor(null);
    setMessages((prev) => prev.map((mm) => {
      if (String(mm._id) !== String(m._id)) return mm;
      const others = (mm.reactions || []).filter((r) => String(r.user) !== myId);
      const cur = (mm.reactions || []).find((r) => String(r.user) === myId);
      const next = !emoji || (cur && cur.emoji === emoji) ? others : [...others, { user: myId, name: me.name, emoji }];
      return { ...mm, reactions: next };
    }));
    custReact(m._id, emoji).catch(() => listCustMessages(conv.id).then(setMessages).catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId, me.name, conv.id]);

  const scrollToMsg = useCallback((id) => {
    const el = id && document.getElementById('kmsg-' + id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('chatmsg-flash');
    setTimeout(() => el.classList.remove('chatmsg-flash'), 1400);
  }, []);

  // Nhảy tới 1 tin từ ô "Tìm trong đoạn chat" (panel bên).
  useEffect(() => {
    const onJump = (e) => { if (String(e.detail?.convId) === String(conv.id)) scrollToMsg(e.detail.id); };
    window.addEventListener('ft:jump-msg', onJump);
    return () => window.removeEventListener('ft:jump-msg', onJump);
  }, [conv.id, scrollToMsg]);

  // Tin KH chưa đọc đầu tiên (để kẻ vạch "Tin nhắn mới").
  const firstUnreadId = (() => {
    const n = unreadSnapRef.current;
    if (!n) return null;
    const incoming = messages.filter((m) => m.direction === 'in');
    if (!incoming.length) return null;
    return incoming[Math.max(0, incoming.length - n)]?._id || null;
  })();

  const messageEls = useMemo(
    () =>
      messages.map((m) => {
        // MỌI tin nhân viên (direction 'out') về BÊN PHẢI — dù là mình hay NV khác — cho nhất quán
        // "phía công ty". Tin của NV khác hiện TÊN THẬT (nội bộ + GĐ biết ai chat); tin của mình không
        // cần tên. Tin KH ở bên trái kèm tên KH. (KH bên 3dfreezy chỉ thấy nhãn phòng ban, không tên thật.)
        const staffSide = m.direction === 'out';
        const mine = staffSide && String(m.sender?._id || '') === myId;
        const isFirstUnread = String(m._id) === String(firstUnreadId);
        const who = staffSide ? (m.sender?.name || m.senderName) : (m.senderName || conv.name);
        return (
          <div className="chatmsg-row" key={m._id} id={'kmsg-' + m._id}>
            {isFirstUnread && (
              <div className="chat-newdivider" ref={unreadRef}>
                <span>Tin nhắn mới</span>
              </div>
            )}
            <div className={'chatmsg' + (staffSide ? ' mine' : '') + (m.pending ? ' pending' : '') + (m.failed ? ' failed' : '')}>
              {/* Tên người viết: tin KH hiện tên KH (trái); tin NV KHÁC hiện tên NV (phải). Tin của mình: không tên. */}
              {!mine && (
                <div className="chatmsg-who">{who}</div>
              )}
              <div className="chatmsg-line">
                <div className="chatmsg-content">
                  {m.replyTo && (
                    <div className="chatmsg-quote" onClick={() => scrollToMsg(m.replyTo.message)} title="Xem tin gốc">
                      <span className="chatmsg-quote-name">{m.replyTo.senderName || 'Tin nhắn'}</span>
                      <span className="chatmsg-quote-text">{m.replyTo.text || 'đã gửi tệp đính kèm'}</span>
                    </div>
                  )}
                  <div
                    className={'chatmsg-body' + (staffSide ? ' mine' : '')}
                    onClick={() => {
                      if (lpFired.current) return; // vừa GIỮ để trả lời -> bỏ qua chạm
                      if (!mine) setReactPickerFor((v) => (v === m._id ? null : m._id));
                    }}
                    onTouchStart={() => { lpFired.current = false; clearTimeout(lpTimer.current); lpTimer.current = setTimeout(() => { lpFired.current = true; setReactPickerFor(null); startReply(m); }, 420); }}
                    onTouchEnd={() => clearTimeout(lpTimer.current)}
                    onTouchMove={() => clearTimeout(lpTimer.current)}
                    title={!mine ? 'Chạm để thả cảm xúc · Giữ để trả lời' : 'Giữ để trả lời'}
                  >
                    {m.text && (
                      <div className={'chatmsg-bubble' + (isBigEmoji(m.text) ? ' big-emoji' : '') + (staffSide ? ' has-tick' : '')}>
                        {linkifyText(m.text)}
                        {staffSide && <DeliveryTick m={m} />}
                      </div>
                    )}
                    {(m.attachments || []).map((a, i) => (
                      <div className="chatmsg-att" key={i}>
                        <AttachmentView a={a} />
                      </div>
                    ))}
                    {staffSide && !m.text && (m.attachments || []).length > 0 && (
                      <div className="chatmsg-tick-float"><DeliveryTick m={m} /></div>
                    )}
                    <div className="chatmsg-corner">
                      <button className="chatmsg-cbtn" title="Trả lời tin này" onClick={(e) => { e.stopPropagation(); startReply(m); }}>↩</button>
                    </div>
                    {!mine && reactPickerFor === m._id && (
                      <div className="chatmsg-react-picker" onClick={(e) => e.stopPropagation()}>
                        {REACTION_EMOJIS.map((e) => (
                          <button key={e} type="button" className="chatmsg-react-opt" onClick={() => doReact(m, e)}>{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(m.reactions || []).length > 0 && (
                <div className="chatmsg-reacts">
                  {Object.entries(
                    (m.reactions || []).reduce((acc, r) => {
                      (acc[r.emoji] = acc[r.emoji] || []).push(r.name || 'Ai đó');
                      return acc;
                    }, {})
                  ).map(([emoji, names]) => {
                    const meReacted = (m.reactions || []).some((r) => String(r.user) === myId && r.emoji === emoji);
                    return (
                      <button
                        key={emoji}
                        type="button"
                        className={'chatmsg-react-badge' + (meReacted ? ' me' : '')}
                        title="Xem ai đã thả cảm xúc"
                        onClick={() => setPeek({ title: 'Cảm xúc', lines: (m.reactions || []).map((r) => ({ icon: r.emoji, name: r.name || 'Ai đó' })) })}
                      >
                        <span>{emoji}</span> {names.length}
                      </button>
                    );
                  })}
                </div>
              )}
              {mine && m.failed ? (
                <div className="chatmsg-time" style={{ color: 'var(--danger)' }}>
                  Gửi lỗi · <button type="button" className="chatmsg-resend" onClick={() => resend(m)}>Gửi lại</button>
                </div>
              ) : (
                <div className="chatmsg-time">{m.pending ? 'Đang gửi…' : fmtMsgTime(m.createdAt)}</div>
              )}
            </div>
          </div>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, myId, firstUnreadId, reactPickerFor, doReact, resend, startReply, scrollToMsg]
  );

  // Kéo giãn cửa sổ (chia sẻ kích thước với chat nội bộ qua ft_chat_size).
  const startResize = useCallback((e) => {
    if (window.innerWidth <= 980) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = panelRef.current?.getBoundingClientRect();
    const startW = rect?.width || 372;
    const startH = rect?.height || 520;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev) => {
      const w = Math.min(720, Math.max(300, startW + (startX - ev.clientX)));
      const h = Math.min(Math.round(window.innerHeight * 0.92), Math.max(360, startH + (startY - ev.clientY)));
      setSize({ w, h });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setSize((s) => { if (s) { try { localStorage.setItem('ft_chat_size', JSON.stringify(s)); } catch {} } return s; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const panelStyle = size && !minimized ? { width: size.w, height: size.h } : undefined;

  return (
    <div ref={panelRef} style={panelStyle} className={'chatpanel chatpanel-conv' + (front ? ' front' : '') + (minimized ? ' min' : '')}>
      {!minimized && <div className="chatpanel-resize" onMouseDown={startResize} title="Kéo để thay đổi kích thước" />}
      {peek && (
        <div className="chat-peek-overlay" onClick={() => setPeek(null)}>
          <div className="chat-peek" onClick={(e) => e.stopPropagation()}>
            <div className="chat-peek-head">
              <b>{peek.title} · {peek.lines.length}</b>
              <button type="button" className="chat-members-x" onClick={() => setPeek(null)} title="Đóng">×</button>
            </div>
            <div className="chat-peek-list">
              {peek.lines.map((l, i) => (
                <div key={i} className="chat-peek-row">
                  <span className="chat-peek-icon">{l.icon}</span>
                  <span className="chat-peek-name">{l.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="chathead" onClick={() => minimized && toggleMinimize(false)}>
        <button className="chathead-btn chathead-back" onClick={(e) => { e.stopPropagation(); onBack ? onBack() : closeWindow(conv.id); }} title="Quay lại danh sách">‹</button>
        <span className="chat-avatar sm chathead-ava cust">{initials(conv.name)}</span>
        <div
          className="chathead-title"
          onClick={onTitleClick ? (e) => { e.stopPropagation(); onTitleClick(); } : undefined}
          style={onTitleClick ? { cursor: 'pointer' } : undefined}
          title={onTitleClick ? 'Bấm để mở/đóng bảng thông tin' : undefined}
        >
          {conv.name}
          {minimized && minUnread > 0 && <span className="chathead-unread" style={{ marginLeft: 8 }}>{minUnread > 99 ? '99+' : minUnread}</span>}
          {!minimized && (
            <span className="small" style={{ opacity: .85, fontWeight: 500 }}>
              {conv.custCode ? ` · ${conv.custCode}` : ''}
              {' · '}{SVC_LABEL[conv.svcType] || 'Kinh doanh'}
            </span>
          )}
        </div>
        {!minimized && canMuteKh(me) && (
          <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); doMute(); }}
            title={muted ? 'Đang tắt âm — bấm để bật lại thông báo' : 'Tắt âm thông báo hội thoại này'}>
            {muted ? '🔕' : '🔔'}
          </button>
        )}
        <button className="chathead-btn chathead-min" onClick={(e) => { e.stopPropagation(); toggleMinimize(); }} title={minimized ? 'Mở lại' : 'Thu nhỏ'}>
          {minimized ? '▢' : '—'}
        </button>
        <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); if (isMobile()) setOpen(false); else closeWindow(conv.id); }} title="Đóng cửa sổ chat">
          ×
        </button>
      </div>
      {/* Tab chuyển KÊNH của cùng khách (chỉ hiện khi khách có >1 kênh mình xem được) */}
      {!minimized && chans.length > 1 && (
        <div className="cust-chantabs">
          {chans.map((ch) => (
            <button
              key={ch.id}
              type="button"
              className={'cust-chantab' + (String(ch.id) === String(conv.id) ? ' on' : '')}
              onClick={() => String(ch.id) !== String(conv.id) && switchChannel(ch)}
            >
              {SVC_LABEL[ch.svcType] || 'Kinh doanh'}
              {ch.unread > 0 && <span className="cust-chantab-badge">{ch.unread > 9 ? '9+' : ch.unread}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="chatbody">
        <div
          className={'chatthread' + (dragOver ? ' dragover' : '')}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
          onDrop={onDrop}
        >
          {dragOver && <div className="chat-droplay">Thả tệp để gửi</div>}
          <div
            className="chatmsgs"
            ref={msgsRef}
            onScroll={() => { const near = isNearBottom(); setScrolledUp(!near); if (newBelow && near) setNewBelow(0); }}
            onClick={(e) => { if (e.target.classList.contains('chat-att-img')) setImgView(e.target.src); }}
          >
            {loadingMsgs ? <div className="chatempty">Đang tải…</div> : messageEls}
            <div ref={endRef} />
          </div>

          {(newBelow > 0 || scrolledUp) && (
            <button type="button" className="chat-jump-latest" onClick={jumpToLatest}>
              {newBelow > 0 ? `↓ ${newBelow} tin nhắn mới` : '↓ Tin mới nhất'}
            </button>
          )}

          {atts.length > 0 && (
            <div className="chat-att-strip">
              {atts.map((a, i) => (
                <div className="chat-att-chip" key={i}>
                  {a.preview ? <img src={a.preview} alt="" /> : <span className="chat-att-doc">📎 {a.name}</span>}
                  <button type="button" className="chat-att-x" onClick={() => setAtts((prev) => prev.filter((_, j) => j !== i))}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {replyTo && (
            <div className="chat-reply-bar">
              <div className="chat-reply-info">
                <span className="chat-reply-label">↩ Trả lời {replyTo.senderName}</span>
                <span className="chat-reply-text">{replyTo.text || 'đã gửi tệp đính kèm'}</span>
              </div>
              <button type="button" className="chat-reply-x" onClick={() => setReplyTo(null)} title="Huỷ trả lời">×</button>
            </div>
          )}

          {imgView && <ImageLightbox src={imgView} onClose={() => setImgView(null)} />}

          <form className="chatcompose" onSubmit={submit}>
            {showEmoji && <EmojiPicker onPick={insertEmoji} onSend={sendSticker} />}
            <button
              ref={emojiBtnRef}
              type="button"
              className={'chathead-btn chat-attbtn' + (showEmoji ? ' on' : '')}
              title="Sticker & emoji"
              onClick={() => setShowEmoji((v) => !v)}
            >
              😊
            </button>
            <button type="button" className="chathead-btn chat-attbtn" title="Đính kèm tệp" onClick={() => fileRef.current?.click()}>
              📎
            </button>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={onPaste}
              onKeyDown={(e) => {
                // MÁY TÍNH: Enter gửi, Shift+Enter xuống dòng. ĐIỆN THOẠI: Enter xuống dòng, gửi bằng nút.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing && !isMobile()) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Nhắn cho khách…"
              autoFocus={front && !isMobile()}
            />
            <button type="submit" className="btn sm" disabled={sending || (!text.trim() && !atts.length)}>
              {sending ? '…' : 'Gửi'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
